import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, ShieldCheck, Heart, Star, Clock, MapPin, GraduationCap, Gift, CalendarCheck, Phone } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Primitives";
import { WaveBottom, StarDoodle, DotsDoodle, BlobDoodle, Squiggle } from "@/components/Decor";
import { api, formatApiError, trackEvent } from "@/lib/api";
import { BUSINESS } from "@/data/site";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const PERKS = [
  { icon: Star, color: "#FFB703", bg: "#FFF3D1", title: "Competitive pay", desc: "$20/hr base plus additional pay for every extra child, paid reliably." },
  { icon: Clock, color: "#FF70A6", bg: "#FFE1EE", title: "Flexible schedule", desc: "You choose the jobs and hours that fit your life — days, nights or weekends." },
  { icon: Heart, color: "#2A9D8F", bg: "#D7F0EC", title: "Meaningful work", desc: "Build real relationships with wonderful DFW families who value you." },
  { icon: MapPin, color: "#4CC9F0", bg: "#DFF6FE", title: "Work near home", desc: "Take placements across Dallas–Fort Worth, close to where you live." },
  { icon: GraduationCap, color: "#8B5CF6", bg: "#EDE6FE", title: "Training & CPR support", desc: "We support your CPR & First Aid certification and ongoing growth." },
  { icon: ShieldCheck, color: "#06D6A0", bg: "#D4F7EC", title: "Respected & backed", desc: "Join a 30-year boutique team that has your back on every single job." },
  { icon: Gift, color: "#E65C92", bg: "#FFE1EE", title: "$200 referral bonus", desc: "Know a great caregiver? Refer them and earn $200 when they're successfully placed." },
  { icon: CalendarCheck, color: "#4CC9F0", bg: "#DFF6FE", title: "Guaranteed hours", desc: "Long-term placements include guaranteed hours — dependable pay you can count on." },
];

const CAREERS_HERO = "https://images.unsplash.com/photo-1595085610896-fb31cfd5d4b7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

const empty = {
  full_name: "", email: "", phone: "", neighborhood: "", years_experience: "",
  cpr_certified: false, education: "", driving_license: false, bio: "",
};

export default function Apply() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !/\S+@\S+\.\S+/.test(form.email) || !form.phone || !form.neighborhood || !form.years_experience) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/applicants", form);
      trackEvent("form_complete", { form: "applicant" });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <>
        <Seo title="Application Received" description="Your sitter application has been received." />
        <div className="container-mbf py-24 max-w-2xl text-center">
          <div className="w-20 h-20 rounded-full bg-sage/15 text-sage flex items-center justify-center mx-auto mb-6"><CheckCircle2 className="w-10 h-10" /></div>
          <h1 className="font-serif text-3xl md:text-4xl text-espresso mb-4" data-testid="apply-success-title">Thank you for applying!</h1>
          <p className="text-base text-stoneish leading-relaxed mb-8">We've received your application. Our team reviews every applicant personally — if you're a fit for our next screening cohort, Jade will be in touch.</p>
          <Link to="/" className="btn-primary" data-testid="apply-success-home">Back to home</Link>
        </div>
      </>
    );
  }

  return (
    <>
      <Seo title="Careers · Become a Sitter" description="Join Mom's Best Friend Sitters — DFW's premier boutique sitter agency. Flexible hours, competitive pay ($20/hr+), and a supportive 30-year team. Apply to become a caregiver today." breadcrumbs={[{ name: "Home", item: "/" }, { name: "Careers", item: "/careers" }]} />

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#FFFDF7] via-[#FFF5DC] to-[#FFE9CC]">
        <StarDoodle className="absolute top-16 left-[5%] w-8 h-8 animate-float-slow hidden md:block" color="#FFB703" />
        <DotsDoodle className="absolute bottom-24 right-[46%] hidden lg:block" color="#4CC9F0" />
        <div className="container-mbf pt-14 md:pt-20 pb-24 grid lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-6">
            <span className="chip mb-6" data-testid="careers-badge"><Heart className="w-3.5 h-3.5 text-coral fill-coral" /> Now hiring caring sitters across DFW</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-espresso">
              Do work you <span className="relative inline-block text-coral">love<Squiggle className="absolute -bottom-2 left-0 w-full h-3" color="#FFB703" /></span>, caring for families
            </h1>
            <p className="mt-7 text-lg text-stoneish leading-relaxed max-w-xl font-medium">Join DFW's most trusted boutique sitter agency. Warm, reliable, experienced caregivers — flexible hours, competitive pay, and a team that truly values you.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href="#apply" className="btn-accent text-base !px-8 !py-4" data-testid="careers-apply-cta">Apply in 2 minutes <ArrowRight className="w-4 h-4" /></a>
              <a href={BUSINESS.phoneHref} className="btn-outline text-base !px-8 !py-4" data-testid="careers-phone-link"><Phone className="w-4 h-4" /> {BUSINESS.phone}</a>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
              <div><div className="font-display font-bold text-3xl text-espresso">$20+</div><div className="text-xs font-semibold text-stoneish">per hour</div></div>
              <span className="h-8 w-px bg-[#E2D7C3]" />
              <div><div className="font-display font-bold text-3xl text-espresso">Flexible</div><div className="text-xs font-semibold text-stoneish">you pick hours</div></div>
              <span className="h-8 w-px bg-[#E2D7C3]" />
              <div><div className="font-display font-bold text-3xl text-espresso">30+ yrs</div><div className="text-xs font-semibold text-stoneish">trusted brand</div></div>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="relative">
              <BlobDoodle className="absolute -top-8 -right-6 w-40 h-40" color="#FF70A6" />
              <div className="relative rounded-[2.5rem] overflow-hidden border-[6px] border-white shadow-pop ring-4 ring-gold/30">
                <img src={CAREERS_HERO} alt="A happy Mom's Best Friend caregiver" className="w-full h-[380px] md:h-[500px] object-cover" />
              </div>
              <div className="absolute -bottom-6 left-2 sm:-left-6 bg-white rounded-[1.5rem] shadow-pop border-2 border-[#F1E6D0] p-5 max-w-[240px]">
                <div className="flex items-center gap-1 mb-1.5">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-gold fill-gold" />)}</div>
                <p className="text-sm text-stoneish leading-snug font-medium">"Best job I've had — flexible hours and families who truly appreciate me."</p>
                <p className="text-xs font-bold text-espresso mt-2">— Maria, sitter since 2019</p>
              </div>
            </div>
          </div>
        </div>
        <WaveBottom color="#FFFFFF" />
      </section>

      {/* PERKS */}
      <section className="container-mbf py-20 md:py-28">
        <div className="max-w-2xl mx-auto text-center mb-14">
          <p className="overline mb-3 justify-center"><Squiggle className="w-8 h-3" color="#2A9D8F" /> Why join us</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-espresso">Perks that make it worth it</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {PERKS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.05}>
              <div className="card-warm p-7 h-full" data-testid={`careers-perk-${i}`}>
                <span className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: p.bg }}><p.icon className="w-7 h-7" style={{ color: p.color }} /></span>
                <h3 className="text-xl font-bold text-espresso mb-2">{p.title}</h3>
                <p className="text-sm text-stoneish leading-relaxed font-medium">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* APPLICATION */}
      <section id="apply" className="bg-parchment-2 border-t border-[#F1E6D0] scroll-mt-24">
        <div className="container-mbf py-20 md:py-28 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-28">
              <p className="overline mb-3">The application</p>
              <h2 className="text-3xl font-extrabold text-espresso mb-4">Tell us about you</h2>
              <p className="text-stoneish leading-relaxed mb-6 font-medium">Takes about two minutes. Our team reviews every applicant personally — every caregiver we place completes our full 11-step screening.</p>
              <ul className="space-y-3">
                {["Warm & reliable", "Experienced with children", "CPR certified (or willing to be)", "Based in the DFW metroplex"].map((x) => (
                  <li key={x} className="flex items-center gap-3 text-sm text-espresso font-semibold"><span className="w-6 h-6 rounded-full bg-mint/20 text-mint flex items-center justify-center shrink-0"><CheckCircle2 className="w-3.5 h-3.5" /></span>{x}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="lg:col-span-2">
            <form onSubmit={submit} className="card-warm p-7 md:p-10 space-y-6" data-testid="apply-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div><Label htmlFor="full_name" className="mb-2 block text-espresso">Full name *</Label><Input id="full_name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="apply-name-input" /></div>
                <div><Label htmlFor="phone" className="mb-2 block text-espresso">Phone *</Label><Input id="phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="apply-phone-input" /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div><Label htmlFor="email" className="mb-2 block text-espresso">Email *</Label><Input id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="apply-email-input" /></div>
                <div><Label htmlFor="neighborhood" className="mb-2 block text-espresso">DFW neighborhood *</Label><Input id="neighborhood" value={form.neighborhood} onChange={(e) => set("neighborhood", e.target.value)} data-testid="apply-neighborhood-input" /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div><Label htmlFor="years" className="mb-2 block text-espresso">Years of experience *</Label><Input id="years" placeholder="e.g. 5 years" value={form.years_experience} onChange={(e) => set("years_experience", e.target.value)} data-testid="apply-experience-input" /></div>
                <div><Label htmlFor="education" className="mb-2 block text-espresso">College / education</Label><Input id="education" value={form.education} onChange={(e) => set("education", e.target.value)} data-testid="apply-education-input" /></div>
              </div>
              <div className="flex flex-col sm:flex-row gap-6">
                <label className="flex items-center gap-3 cursor-pointer" data-testid="apply-cpr-label">
                  <Checkbox checked={form.cpr_certified} onCheckedChange={(v) => set("cpr_certified", !!v)} data-testid="apply-cpr-checkbox" />
                  <span className="text-sm text-espresso">I am CPR & First Aid certified</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer" data-testid="apply-license-label">
                  <Checkbox checked={form.driving_license} onCheckedChange={(v) => set("driving_license", !!v)} data-testid="apply-license-checkbox" />
                  <span className="text-sm text-espresso">I have a valid driver's license</span>
                </label>
              </div>
              <div><Label htmlFor="bio" className="mb-2 block text-espresso">Tell us about yourself</Label><Textarea id="bio" rows={5} placeholder="Your experience, why you love working with children, availability…" value={form.bio} onChange={(e) => set("bio", e.target.value)} data-testid="apply-bio-input" /></div>
              <button type="submit" disabled={submitting} className="btn-accent w-full !py-4 disabled:opacity-60" data-testid="apply-submit-btn">
                {submitting ? "Submitting…" : "Submit application"} <ArrowRight className="w-4 h-4" />
              </button>
              <p className="text-xs text-stoneish text-center">Questions? Call us at {BUSINESS.phone}. All applicants complete our full 11-step screening.</p>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
